'use client';

// Consistency Rule · "Consistency calculator" (Figma 12071:59916 desktop
// 792×484, 12471:7889 mobile 366×1006). A working client-side calculator:
// the default state (25% · $1000 · $4000) reproduces the Figma numbers.
import { useId, useState } from 'react';
import { cn } from '@/lib/utils';
import { FigureFrame } from '../primitives';

const LIMIT_MIN = 10;
const LIMIT_MAX = 35;
const DEFAULTS = { limit: 25, bestDay: 1000, target: 4000 };

const money = (n: number) =>
  `$${Math.max(0, Math.round(n)).toLocaleString('en-US')}`;

const digits = (s: string) => s.replace(/[^\d]/g, '').slice(0, 9);

/** Donut ring: 184px, 7% stroke, arc from 12 o'clock clockwise (Figma "Anel · 25%"). */
function Ring({ share }: { share: number }) {
  const size = 184;
  const stroke = 12.88;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div
      data-ui="figure.consistencyCalculator.ring"
      className="relative flex h-[23rem] w-[23rem] shrink-0 flex-col items-center justify-center"
    >
      <svg viewBox={`0 0 ${size} ${size}`} className="absolute inset-0 h-full w-full" aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#228e71" strokeOpacity={0.12} strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#228e71"
          strokeWidth={stroke}
          strokeLinecap="butt"
          strokeDasharray={`${c * share} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          className="transition-[stroke-dasharray]"
        />
      </svg>
      <p className="relative mb-0 whitespace-nowrap font-geist text-[3rem] font-medium leading-[4rem] text-[#228e71]">
        {Math.round(share * 100)}%
      </p>
      <p className="relative mb-0 whitespace-nowrap text-center font-geist text-[2rem] font-normal leading-[2.5rem] text-black/40">
        max. best
        <br />
        day share
      </p>
    </div>
  );
}

function MoneyInput({
  label,
  value,
  onChange,
  id,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  id: string;
}) {
  return (
    <div className="flex w-full min-w-0 flex-col gap-[1.5rem] lg:flex-1">
      <label htmlFor={id} className="whitespace-nowrap font-normal text-black/[0.64]">
        {label}
      </label>
      <div className="flex h-[5.5rem] w-full items-center gap-[2rem] rounded-[0.25rem] border border-black/10 bg-white px-[1.5rem] lg:gap-[1rem]">
        <span className="font-normal text-black/40">$</span>
        <input
          id={id}
          type="text"
          inputMode="numeric"
          value={value}
          onChange={(e) => onChange(digits(e.target.value))}
          className="h-full min-w-0 flex-1 bg-transparent p-0 font-geist text-[2rem] font-medium leading-[3rem] text-black outline-none"
        />
      </div>
    </div>
  );
}

export default function ConsistencyCalculator() {
  const uid = useId();
  const [limit, setLimit] = useState(DEFAULTS.limit);
  const [bestDayRaw, setBestDayRaw] = useState(String(DEFAULTS.bestDay));
  const [targetRaw, setTargetRaw] = useState(String(DEFAULTS.target));

  const bestDay = Number(bestDayRaw) || 0;
  const target = Number(targetRaw) || 0;
  const share = limit / 100;
  const minTotal = share > 0 ? bestDay / share : 0;
  const otherDays = Math.max(0, minTotal - bestDay);
  const maxBestDay = target * share;
  const fill = ((limit - LIMIT_MIN) / (LIMIT_MAX - LIMIT_MIN)) * 100;

  return (
    <FigureFrame data-ui="figure.consistencyCalculator" padding="p-[2rem] lg:p-[3rem]" className="gap-[2.5rem] [text-wrap:wrap]">
      <div className="flex w-full flex-col gap-[0.5rem] font-geist">
        <p className="mb-0 whitespace-nowrap text-[2.5rem] font-medium leading-[3.5rem] text-black">Consistency calculator</p>
        <p className="mb-0 text-[2rem] font-normal leading-[3rem] text-black/[0.64]">
          Try your own numbers — set the limit for your product.
        </p>
      </div>

      <div
        data-ui="figure.consistencyCalculator.body"
        className="flex w-full flex-col items-center gap-[4rem] font-geist text-[2rem] leading-[3rem] lg:flex-row lg:items-start"
      >
        {/* Ring + legend */}
        <div className="flex w-full flex-col items-center gap-[2rem] lg:w-auto">
          <Ring share={share} />
          <div className="flex w-full flex-col gap-[0.75rem] lg:w-auto">
            <div className="flex items-center gap-[1rem]">
              <span className="h-[1.25rem] w-[1.25rem] shrink-0 rounded-[0.25rem] bg-[#228e71]" />
              <span className="font-normal text-black/[0.64]">Biggest winning day</span>
            </div>
            <div className="flex items-center gap-[1rem]">
              <span className="h-[1.25rem] w-[1.25rem] shrink-0 rounded-[0.25rem] border border-black/10 bg-[#228e71]/[0.12]" />
              <span className="font-normal text-black/[0.64]">All other days</span>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex w-full min-w-0 flex-col gap-[2rem] lg:flex-1">
          {/* Slider */}
          <div data-ui="figure.consistencyCalculator.slider" className="flex w-full flex-col gap-[1.5rem]">
            <div className="flex w-full items-start justify-between">
              <label htmlFor={`${uid}-limit`} className="whitespace-nowrap font-normal text-black/[0.64]">
                Consistency limit
              </label>
              <span className="whitespace-nowrap font-medium text-black">{limit}%</span>
            </div>
            <div className="relative h-[2rem] w-full rounded-[0.25rem]">
              <div className="absolute left-0 top-[0.75rem] h-[0.5rem] w-[94.32%] rounded-[0.25rem] bg-black/10" />
              <div
                className="absolute left-0 top-[0.75rem] h-[0.5rem] rounded-[0.25rem] bg-[#228e71]"
                style={{ width: `calc(94.32% * ${fill / 100})` }}
              />
              <span
                aria-hidden="true"
                className="pointer-events-none absolute top-0 h-[2rem] w-[2rem] rounded-full border-2 border-[#228e71] bg-white"
                style={{ left: `calc(94.32% * ${fill / 100} - 1rem)` }}
              />
              <input
                id={`${uid}-limit`}
                type="range"
                min={LIMIT_MIN}
                max={LIMIT_MAX}
                step={1}
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                aria-label="Consistency limit"
                aria-valuetext={`${limit}%`}
                className="absolute left-0 top-0 m-0 h-[2rem] w-[94.32%] cursor-pointer appearance-none bg-transparent opacity-0 [&::-moz-range-thumb]:h-[2rem] [&::-moz-range-thumb]:w-[2rem] [&::-moz-range-thumb]:border-0 [&::-webkit-slider-thumb]:h-[2rem] [&::-webkit-slider-thumb]:w-[2rem] [&::-webkit-slider-thumb]:appearance-none"
              />
            </div>
          </div>

          {/* Inputs */}
          <div data-ui="figure.consistencyCalculator.fields" className="flex w-full flex-col gap-[1.5rem] lg:flex-row lg:items-start">
            <MoneyInput id={`${uid}-best`} label="Biggest winning day" value={bestDayRaw} onChange={setBestDayRaw} />
            <MoneyInput id={`${uid}-target`} label="Profit target" value={targetRaw} onChange={setTargetRaw} />
          </div>

          {/* Results */}
          <div data-ui="figure.consistencyCalculator.results" className="flex w-full flex-col gap-[1.5rem] lg:flex-row lg:items-stretch">
            <div className="flex w-full min-w-0 flex-col gap-[0.5rem] rounded-[0.25rem] bg-[#e2f3ef] p-[2rem] lg:flex-1">
              <p className="mb-0 whitespace-nowrap font-medium text-[#196551]">Minimum total profit</p>
              <p className="mb-0 whitespace-nowrap text-[3.5rem] font-medium leading-[4.5rem] text-[#196551]">{money(minTotal)}</p>
              <p className="mb-0 font-normal text-[#196551]/80">
                Biggest winning day ÷ consistency limit — all other days together need at least {money(otherDays)}.
              </p>
            </div>
            <div className="flex w-full min-w-0 flex-col gap-[0.5rem] rounded-[0.25rem] bg-[#e2f3ef] p-[2rem] lg:flex-1">
              <p className="mb-0 whitespace-nowrap font-medium text-[#196551]">Max. biggest winning day</p>
              <p className="mb-0 whitespace-nowrap text-[3.5rem] font-medium leading-[4.5rem] text-[#196551]">{money(maxBestDay)}</p>
              <p className="mb-0 font-normal text-[#196551]/80">
                Profit target × consistency limit — no single day may be bigger than this.
              </p>
            </div>
          </div>
        </div>
      </div>
    </FigureFrame>
  );
}
